import React from 'react';
import { View, Image, ScrollView, TouchableOpacity, StyleSheet, Text } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Picker } from '@react-native-picker/picker';
import FormInput from '../common/FormInput';
import { MaterialIcons } from '@expo/vector-icons';
import { INDUSTRIES, BUSINESS_TYPES } from '../../utils/profileConstants';

const BusinessForm = ({
  businessDetails = {},
  onBusinessDetailsChange,
  businessPhotos,
  onBusinessPhotosChange,
  errors = {}
}) => {
  // Ensure businessPhotos is always an array
  const photos = Array.isArray(businessPhotos) ? businessPhotos : [];

  const handleChange = (field, value) => {
    onBusinessDetailsChange({
      ...businessDetails,
      [field]: value
    });
  };

  const handleAddPhoto = async () => {
    try {
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (!permissionResult.granted) {
        alert('Permission to access camera roll is required!');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled) {
        onBusinessPhotosChange([...photos, result.assets[0].uri]);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      alert('Error selecting image. Please try again.');
    }
  };

  const handleRemovePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onBusinessPhotosChange(newPhotos);
  };

  return (
    <View style={styles.container}>
      <FormInput
        label="Business Name"
        value={businessDetails.businessName || ''}
        onChangeText={(value) => handleChange('businessName', value)}
        error={errors.businessName}
        placeholder="Enter your business name"
      />

      <View style={styles.pickerField}>
        <Text style={styles.label}>Industry/Sector</Text>
        <View style={styles.pickerWrapper}>
          <Picker
            selectedValue={businessDetails.industry || ''}
            onValueChange={(value) => handleChange('industry', value)}
            style={styles.picker}
            mode="dropdown"
          >
            <Picker.Item label="Select Industry" value="" />
            {INDUSTRIES.map((industry) => (
              <Picker.Item key={industry} label={industry} value={industry} />
            ))}
          </Picker>
        </View>
      </View>

      <View style={styles.pickerField}>
        <Text style={styles.label}>Business Type</Text>
        <View style={styles.pickerWrapper}>
          <Picker
            selectedValue={businessDetails.businessType || ''}
            onValueChange={(value) => handleChange('businessType', value)}
            style={styles.picker}
            mode="dropdown"
          >
            <Picker.Item label="Select Business Type" value="" />
            {BUSINESS_TYPES.map((type) => (
              <Picker.Item key={type} label={type} value={type} />
            ))}
          </Picker>
        </View>
      </View>

      <FormInput
        label="Business Description"
        value={businessDetails.description || ''}
        onChangeText={(value) => handleChange('description', value)}
        error={errors.description}
        placeholder="Brief description of your business"
        multiline
        numberOfLines={3}
      />

      <FormInput
        label="Years in Operation"
        value={businessDetails.yearsInOperation || ''}
        onChangeText={(value) => handleChange('yearsInOperation', value)}
        error={errors.yearsInOperation}
        placeholder="Years active"
        keyboardType="numeric"
      />

      <View style={styles.photosSection}>
        <Text style={styles.label}>Business Photos</Text>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.photosList}>
          {photos.map((photo, index) => (
            <View key={index} style={styles.photoContainer}>
              <Image source={{ uri: photo }} style={styles.photo} />
              <TouchableOpacity
                style={styles.removeButton}
                onPress={() => handleRemovePhoto(index)}
              >
                <MaterialIcons name="close" size={16} color="#fff" />
              </TouchableOpacity>
            </View>
          ))}

          <TouchableOpacity onPress={handleAddPhoto} style={styles.addPhotoButton}>
            <MaterialIcons name="add-a-photo" size={28} color="#94A3B8" />
            <Text style={styles.addPhotoText}>Add Photo</Text>
          </TouchableOpacity>
        </ScrollView>
        {errors.photos && <Text style={styles.errorText}>{errors.photos}</Text>}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
    width: '100%',
  },
  pickerField: {
    marginBottom: 12,
  },
  label: {
    fontSize: 12,
    marginBottom: 6,
    color: '#64748B',
    fontWeight: '600',
    marginLeft: 2,
  },
  pickerWrapper: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1.2,
    borderColor: '#EDF2F7',
    borderRadius: 10,
    overflow: 'hidden',
  },
  picker: {
    height: 44,
    width: '100%',
    color: '#1E293B',
  },
  photosSection: {
    marginTop: 8,
  },
  photosList: {
    flexDirection: 'row',
  },
  photoContainer: {
    marginRight: 10,
    marginTop: 6,
    position: 'relative',
  },
  photo: {
    width: 64,
    height: 64,
    borderRadius: 10,
  },
  removeButton: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#F87171',
    borderRadius: 8,
    width: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  addPhotoButton: {
    width: 64,
    height: 64,
    borderWidth: 2,
    borderColor: '#CBD5E1',
    borderStyle: 'dashed',
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    marginTop: 6,
  },
  addPhotoText: {
    color: '#64748B',
    marginTop: 2,
    fontSize: 9,
    fontWeight: '600',
  },
  errorText: {
    color: '#EF4444',
    fontSize: 11,
    marginTop: 6,
    marginLeft: 4,
    fontWeight: '500',
  },
});

export default BusinessForm;
